/**
 * Authenticated officer live session (Driver / Chair Dashboard / Observer).
 *
 * QR boundary (do not blur these lanes):
 * - Officers: join via deep link `?session=<uuid>&seat=driver|prompt|observer`
 *   backed by `meeting_sessions` + Supabase Realtime.
 * - Assembly members: scan `/r/:joinCode` (meeting_rooms) — no login, device_token only.
 *   Vote helpers stay in voteRemote.js; voters never touch meeting_sessions.
 */

import { getSupabaseClient } from '../lib/supabaseClient';

/** @typedef {'driver' | 'prompt' | 'observer'} OfficerSeat */

export function mapCloudSeatToUi(driverSeat) {
  return driverSeat === 'chair' ? 'remote' : 'local';
}

export function mapUiSeatToCloud(driverSeat) {
  return driverSeat === 'remote' ? 'chair' : 'driver';
}

export function officerSessionUrl({
  orgId,
  sessionId,
  seat = 'driver',
  origin = typeof window !== 'undefined' ? window.location.origin : '',
}) {
  const params = new URLSearchParams();
  if (sessionId) params.set('session', sessionId);
  if (seat && seat !== 'driver') params.set('seat', seat);
  if (seat === 'prompt') params.set('chairPrompt', '1');
  if (seat === 'observer') params.set('observer', '1');
  const qs = params.toString();
  const hash = seat === 'observer' ? '#observer' : seat === 'prompt' ? '#chair-prompt' : '';
  return `${origin}/orgs/${orgId}/meeting${qs ? `?${qs}` : ''}${hash}`;
}

function normalizeEvents(events) {
  if (Array.isArray(events)) return events;
  if (typeof events === 'string') {
    try {
      const parsed = JSON.parse(events);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function normalizeSessionRow(row) {
  if (!row) return null;
  return {
    id: row.session_id || row.sessionId || row.id,
    organizationId: row.organization_id || row.organizationId,
    meetingId: row.meeting_id || row.meetingId || null,
    status: row.status || 'live',
    driverSeat: row.driver_seat || row.driverSeat || 'driver',
    state: row.state || {},
    revision: Number(row.revision || 1),
    chairSignal: row.chair_signal || row.chairSignal || null,
    events: normalizeEvents(row.events),
    createdAt: row.created_at || row.createdAt || null,
    updatedAt: row.updated_at || row.updatedAt || null,
  };
}

export async function ensureLiveMeetingSession(organizationId, meetingId = null) {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase.rpc('ensure_live_meeting_session', {
    p_organization_id: organizationId,
    p_meeting_id: meetingId || null,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return normalizeSessionRow(row);
}

export async function getLiveMeetingSession(organizationId) {
  const supabase = getSupabaseClient();
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('get_live_meeting_session', {
    p_organization_id: organizationId,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return normalizeSessionRow(row);
}

export async function fetchMeetingSessionById(sessionId) {
  const supabase = getSupabaseClient();
  if (!supabase || !sessionId) return null;
  const withEvents =
    'id, organization_id, meeting_id, status, driver_seat, state, revision, chair_signal, events, created_at, updated_at';
  const withoutEvents =
    'id, organization_id, meeting_id, status, driver_seat, state, revision, chair_signal, created_at, updated_at';

  let { data, error } = await supabase
    .from('meeting_sessions')
    .select(withEvents)
    .eq('id', sessionId)
    .maybeSingle();

  if (error && /events/i.test(error.message || '')) {
    ({ data, error } = await supabase
      .from('meeting_sessions')
      .select(withoutEvents)
      .eq('id', sessionId)
      .maybeSingle());
  }
  if (error) throw error;
  if (!data) return null;
  return normalizeSessionRow({
    session_id: data.id,
    ...data,
  });
}

export async function pushMeetingSessionState(
  sessionId,
  state,
  { expectedRevision = null, driverSeat = null } = {}
) {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase.rpc('push_meeting_session_state', {
    p_session_id: sessionId,
    p_state: state || {},
    p_expected_revision: expectedRevision,
    p_driver_seat: driverSeat,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return {
    id: row.session_id || row.sessionId,
    revision: Number(row.revision),
    driverSeat: row.driver_seat || row.driverSeat,
    updatedAt: row.updated_at || row.updatedAt,
  };
}

export async function postMeetingSessionSignal(sessionId, signal) {
  return postMeetingSessionEvent(sessionId, signal);
}

export async function postMeetingSessionEvent(sessionId, kind, body = null) {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase.rpc('post_meeting_session_event', {
    p_session_id: sessionId,
    p_kind: kind,
    p_body: body,
  });
  if (error) {
    // Fallback for DBs that only have the legacy signal RPC
    if (kind === 'ready' || kind === 'hold' || kind === 'take_control' || kind === 'return_control') {
      const legacy = await supabase.rpc('post_meeting_session_signal', {
        p_session_id: sessionId,
        p_signal: kind,
      });
      if (legacy.error) throw legacy.error;
      const row = Array.isArray(legacy.data) ? legacy.data[0] : legacy.data;
      return {
        id: row.session_id || row.sessionId,
        chairSignal: row.chair_signal || row.chairSignal,
        revision: Number(row.revision),
        events: [],
        updatedAt: row.updated_at || row.updatedAt,
      };
    }
    throw error;
  }
  const row = Array.isArray(data) ? data[0] : data;
  return {
    id: row.session_id || row.sessionId,
    chairSignal: row.chair_signal || row.chairSignal,
    revision: Number(row.revision),
    events: normalizeEvents(row.events),
    updatedAt: row.updated_at || row.updatedAt,
  };
}

export async function postDriverMessageToChair(sessionId, message) {
  return postMeetingSessionEvent(sessionId, 'driver_message', String(message || '').trim());
}

export async function postChairMessageToDriver(sessionId, message) {
  return postMeetingSessionEvent(sessionId, 'chair_message', String(message || '').trim());
}

export async function setMeetingSessionDriverSeat(sessionId, driverSeat) {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase.rpc('set_meeting_session_driver_seat', {
    p_session_id: sessionId,
    p_driver_seat: driverSeat,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return {
    id: row.session_id || row.sessionId,
    driverSeat: row.driver_seat || row.driverSeat,
    revision: Number(row.revision),
    updatedAt: row.updated_at || row.updatedAt,
  };
}

export async function endMeetingSession(sessionId) {
  const supabase = getSupabaseClient();
  if (!supabase || !sessionId) return false;
  const { data, error } = await supabase.rpc('end_meeting_session', {
    p_session_id: sessionId,
  });
  if (error) throw error;
  return Boolean(data);
}

/**
 * Subscribe to postgres changes on a meeting_sessions row.
 * @returns {() => void} unsubscribe
 */
export function subscribeMeetingSession(sessionId, onRow) {
  const supabase = getSupabaseClient();
  if (!supabase || !sessionId) return () => {};

  const channel = supabase
    .channel(`meeting_session:${sessionId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'meeting_sessions',
        filter: `id=eq.${sessionId}`,
      },
      (payload) => {
        const row = payload.new || payload.old;
        if (!row) return;
        onRow(
          normalizeSessionRow({
            session_id: row.id,
            ...row,
          }),
          payload.eventType
        );
      }
    )
    .subscribe((status, err) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        console.warn('[officerSession] realtime status', status, err?.message || err);
      }
    });

  return () => {
    try {
      void supabase.removeChannel(channel);
    } catch {
      /* ignore */
    }
  };
}
